<script setup lang="ts">
import {computed, onMounted, ref, watch} from "vue";
import {useRouter, useRoute} from 'vue-router';
import {createCrewList} from "@/services/crew.service";
import {useI18n} from 'vue-i18n';
import CustomModal from "@/components/CustomModal/CustomModal.vue";
import CustomButton from "@/components/Button/Button.vue";
import {useCrewStore} from '@/stores/crewStore';
import {useUserStore} from '@/stores/userStore';
import CrewListsDeleteModal from "@/pages/CrewLists/components/CrewListsDeleteModal.vue";
import CrewListsEditModal from "@/pages/CrewLists/components/CrewListsEditModal.vue";
import TripDeleteModal from "@/components/TripCard/TripDeleteModal.vue";
import {useWindowSize} from "@vueuse/core";
import CustomSpinner from "@/components/CustomSpinner/CustomSpinner.vue";
import {VueSpinner} from "vue3-spinners";


const {t} = useI18n(); // Initialize i18n

const router = useRouter();
const route = useRoute();

const showDeleteModal = ref(false);
const showEditModal = ref(false);

const crewlist = ref(null);
const crewStore = useCrewStore();
const userStore = useUserStore();

function navigateToCrewDetails(uuid: string) {
  // const list = crewStore.crewList.find(item => item.uuid === uuid);
  // if (list) {
  //   crewStore.crewListName = list.listName;
  //   console.log(crewStore.crewListName)
  // }
  router.push(`/my/crew-list/${uuid}`);
}

async function onDelete(uuid: string) {
  await crewStore.deleteCrewList(uuid);
  showDeleteModal.value = false;
  await crewStore.fetchAllCrewLists();
  showDeleteModal.value = false;
}

async function onEdit({uuid, name}: { uuid: string; name: string }) {
  await crewStore.updateCrewList(uuid, name);
  showEditModal.value = false;
  await crewStore.fetchAllCrewLists();
  showEditModal.value = false;
}


const showAddNewCrewList = ref(false);
const newCrewListName = ref("");
const cardColors = ['#FAFFD8', '#FFF3EB', '#EBFFED'];
const isLoading = ref(true);

const isCreatingList = ref(false);
const createError = ref<string | null>(null);

function resetCreateForm() {
  newCrewListName.value = "";
  createError.value = null;
  isCreatingList.value = false;
}

function handleCreateModalClose() {
  showAddNewCrewList.value = false;
  resetCreateForm();
}

// Watch for session restoration to trigger modal logic and data fetching
watch(
    () => userStore.sessionRestored,
    async (restored) => {
      if (restored) {
        const shouldOpen =
            route.query.modal === 'create' ||
            (typeof window !== 'undefined' && localStorage.getItem('crewCreateModal') === '1');
        if (shouldOpen) {
          showAddNewCrewList.value = true;
          try {
            localStorage.removeItem('crewCreateModal');
          } catch {
          }
        }
        try {
          isLoading.value = true;
          await crewStore.fetchAllCrewLists();
        } finally {
          isLoading.value = false;
        }
      }
    },
    {immediate: true}
);

async function addNewCrewList() {
  createError.value = null;
  const name = newCrewListName.value.trim();
  if (!name) return;

  try {
    isCreatingList.value = true;
    await createCrewList(name);
    await crewStore.fetchAllCrewLists();
    // Close and reset on success
    handleCreateModalClose();
  } catch (e: any) {
    createError.value = e?.response?.data?.message || e?.message || 'Failed to create crew list. Please try again.';
  } finally {
    isCreatingList.value = false;
  }
}

function getInitials(name = '') {
  const [first = '', last = ''] = name.trim().split(' ');
  return `${first[0] || ''}${last[0] || ''}`.toUpperCase();
}

watch(showAddNewCrewList, (newValue) => {
  if (newValue) {
    document.documentElement.style.overflow = 'hidden';
  } else {
    document.documentElement.style.overflow = 'auto';
    // clear modal
    resetCreateForm();
  }
});

const {width} = useWindowSize();
const isMobile = computed(() => {
  const value = width.value <= 500;
  return value;
});


</script>

<template>
  <div class="flex flex-col container py-12 gap-8">
    <div class="flex justify-between align-center">
      <h1 class="responsive-title-3 font-semibold header">{{ $t('crewList.header') }}</h1>
      <div class="px-5" v-if="crewStore.crewList.length !== 0 && !isMobile">
        <CustomButton
            type="black"
            class="self-end responsive-body-medium w-fit h-8 btnText"
            :outlined="false"
            :rounded="true"
            @click="showAddNewCrewList = true">
          {{ $t('crewList.addNewList') }}
        </CustomButton>
      </div>
    </div>
    <div v-if="!isLoading">
      <div :class="`crew-grid ${crewStore.crewList.length == 0 ? 'content-center min-h-[68.5vh]' : ''}`">
        <div v-if="crewStore.crewList.length === 0"
             class="flex justify-center items-center h-full gap-5 min-h-[400px] alignBoxClass">
          <div class="flex flex-col bg-background-light boxClass justify-between rounded-8xl p-7.5 ">
            <div>
              <img src="@/assets/icons/svg/Crew_40x40.svg" alt="Crew">
            </div>
            <div class=" responsive-body-large smallText font-semibold">{{ $t('crewList.noCrewList') }}</div>
          </div>
          <div @click="showAddNewCrewList = true"
               class="flex flex-col bg-primary-blue text-white justify-center items-center rounded-8xl p-7.5 gap-1 cursor-pointer boxClass"
               type="primary">
            <img src="@/assets/icons/svg/Add_24x24_white.svg" alt="Plus" width="44"/>
            <div class=" responsive-body-large font-semibold text-center">{{ $t('crewList.addNewList') }}</div>
          </div>
        </div>
        <div
            v-else-if="crewStore.crewList.length !== 0"
            class="crew-grid-content grid 2xl:grid-cols-4 lg:grid-cols-3 grid-cols-3 gap-6 mt-5"
        >
          <div
              v-for="(crew, index) in crewStore.crewList"
              :key="crew.uuid"
              class="flex flex-col gap-8 py-6 px-5 rounded-2xl"
              :style="{ backgroundColor: cardColors[index % cardColors.length] }"
          >
            <div class="flex justify-between items-center">
              <div class="responsive-title-5 font-semibold">
                {{ crew.listName }}
              </div>

              <div class=" w-12 h-7 flex justify-end rounded-11xl">
                <ElDropdown trigger="click"
                            placement="left"
                            :teleported="false"
                            :popper-options="{
                      modifiers: [
                        {
                          name: 'preventOverflow',
                          options: {
                            boundary: 'viewport',
                            padding: 20
                          }
                        },
                        {
                          name: 'offset',
                          options: {
                            offset: [0, 10]
                          }
                        }
                      ]
                    }">
                  <img src="@/assets/icons/svg/Menu dots_24x24.svg">
                  <template #dropdown>
                    <ElDropdownMenu>
                      <ElDropdownItem class="text-black flex gap-2"
                                      @click="() => { crewlist = crew; showEditModal = true; }">
                        <img src="@/assets/icons/svg/Edit_24x24.svg"/>
                        {{ t('crewList.editList') }}
                      </ElDropdownItem>
                      <ElDropdownItem class="text-black flex gap-2"
                                      @click="() => { crewlist = crew; showDeleteModal = true; }">
                        <img src="@/assets/icons/svg/Delete_24x24.svg"/>
                        {{ t('crewList.deleteList') }}
                      </ElDropdownItem>
                    </ElDropdownMenu>
                  </template>
                </ElDropdown>
              </div>
            </div>

            <div class="h-7.5 w-[60%]">
              <div class="flex -space-x-2">
                <div
                    v-for="(member) in crew.members.slice(0, 10)" :key="member.uuid"
                    class=" flex-shrink-0 relative inline-flex h-[30px] w-[30px] items-center justify-center rounded-24xl border-2 border-white bg-bw-silver font-bold responsive-body-xsmall text-black"
                >
                  <img
                      v-if="member.profileImage"
                      :src="member.profileImage"
                      :alt="member.name"
                      class="h-full w-full rounded-24xl object-cover "
                      width="30"
                  />
                  <span v-else>{{ getInitials(member.name) }}</span>
                </div>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <CustomButton
                  type="outline-black"
                  size="outlinesmall"
                  :rounded="true"
                  class="border border-black px-5 py-2 rounded-12xl w-fit arrow-btn"
                  @click="navigateToCrewDetails(crew.uuid)"
              >
                <img
                    src="@/assets/icons/svg/Arrow_right_16x16.svg"
                    alt="arrow"
                    class="icon-default"
                />
                <img
                    src="@/assets/icons/svg/Arrow_right_white_16x16.svg"
                    alt="arrow hover"
                    class="icon-hover"
                />
              </CustomButton>
              <div class="text-sm">
                <span class="font-semibold">{{ crew.members?.length ?? 0 }}</span>
                {{ $t('crewList.members') }}
              </div>
            </div>
          </div>
        </div>
        <div class="flex justify-center items-center w-full mt-4 crewlist"
             v-if="crewStore.crewList.length !== 0 && isMobile">
          <CustomButton
              type="black"
              class="w-full h-8 responsive-body-medium"
              :outlined="false"
              :rounded="true"
              @click="showAddNewCrewList = true">
            {{ $t('crewList.addNewList') }}
          </CustomButton>
        </div>
      </div>
    </div>
    <div v-else class="flex justify-center items-center flex-col gap-8 py-12 min-h-52">
      <div class="w-fit px-5 py-2 rounded-22xl">
        <CustomSpinner/>
      </div>
    </div>
  </div>
  <CustomModal :show="showAddNewCrewList" @close="handleCreateModalClose" classes="w-1/6" :mobile-fullscreen="false"
               :custom-index="9999" extra-classes="h-fit" drawerHeight="fit-content">
    <div class="flex flex-col p-3 sm:p-6 sm:min-w-[346px] sm:min-h-[243px]">
      <div class="cursor-pointer self-end " type="default" @click="handleCreateModalClose">
        <img src="@/assets/icons/svg/Close_16x16.svg" alt="Close" width="18"/>
      </div>
      <div class="flex flex-col sm:py-8 sm:px-4">

        <div class="responsive-title-4 pb-10 font-semibold">{{ $t('crewList.createNewList') }}</div>

        <div class="flex flex-col">
          <VTextField
              v-model="newCrewListName"
              :label="$t('crewList.listNamePlaceholder')"
              type="name"
              variant="underlined"
              :disabled="isCreatingList">
          </VTextField>
        </div>
        <div v-if="createError" class="text-notifications-red responsive-body-xsmall mb-2">
          {{ createError }}
        </div>
        <div class="flex justify-between align-center">
          <CustomButton
              type="black"
              class="self-start responsive-body-medium w-28 h-13"
              :outlined="false"
              size="medium"
              :rounded="true"
              :disabled="isCreatingList || !newCrewListName?.trim()"
              @click="addNewCrewList">
            <div class="flex items-center gap-2">
              <VueSpinner v-if="isCreatingList" :size="16" color="#FFFFFF"/>
              <span>{{ $t('crewList.createButton') }}</span>
            </div>
          </CustomButton>
        </div>
      </div>
    </div>
  </CustomModal>


  <CrewListsDeleteModal
      v-if="crewlist"
      :showDeleteModal="showDeleteModal"
      @on-close="() => { showDeleteModal = false; }"
      :crewlist="crewlist"
      @on-delete-click="() => {
        console.log('Attempting to delete crewlist:', crewlist);
        if (crewlist?.uuid) onDelete(crewlist.uuid);
      }"
  />

  <CrewListsEditModal
      v-if="crewlist"
      :showEditModal="showEditModal"
      :name="crewlist?.listName"
      :crewlist="crewlist"
      @on-close="showEditModal = false"
      @on-edit-click="(newName) => onEdit({ uuid: crewlist?.uuid, name: newName })"
  />
</template>

<style scoped>

.iconClass {
  height: 30px !important;
  width: 30px !important;
}

.arrow-btn {
  position: relative;
}

.arrow-btn .icon-hover {
  display: none;
}

.arrow-btn:hover .icon-default {
  display: none;
}

.arrow-btn:hover .icon-hover {
  display: inline;
}

.circle-container {
  display: flex;
  gap: 10px;
  /* Space between circles */
  position: relative;
}

.circle-container img {
  width: 30px;
  /* Adjust the size as needed */
  height: 30px;
  /* Adjust the size as needed */
  border-radius: 50%;
  /* White border around the image */
  position: absolute;
}

.circle-container img:nth-child(1) {
  left: 0;
}

.circle-container img:nth-child(2) {
  left: 20px;
  /* Adjust this value to control the overlap */
}

.circle-container img:nth-child(3) {
  left: 40px;
  /* Adjust this value to control the overlap */
}

.border-black {
  border-color: black !important;
}

.CardsFlexDirection > div {
  min-width: 420px;
}

.boxClass {
  min-width: 200px;
  max-width: 220px;
  min-height: 240px;
  max-height: 260px;
}

@media (max-width: 768px) {
  .boxClass {
    min-width: 150px;
    max-width: 180px;
    min-height: 180px;
    max-height: 200px;
  }

  .CardsFlexDirection {
    flex-direction: column !important;
    justify-content: center;
  }

  .CardsFlexDirection > div {
    min-width: 320px !important;
  }

  .btnText {
    font-size: 12px !important;
    min-width: 100px !important;
  }

  .header {
    font-size: 23px;
  }
}

@media (max-width: 786px) and (min-width: 541px) {
  .crew-grid-content {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 540px) {
  .crew-grid {
    align-content: start;
    min-height: 20rem;
  }

  .box-class {
    min-width: 42vw;
    height: 11.4375rem;
  }

  .box-class img[alt="Plus"] {
    height: 2.75rem;
    width: 2.75rem;
  }

  .box-class img[alt="Suitcase"] {
    height: 1.875rem;
    width: 1.875rem;
  }

  .crew-grid-content {
    grid-template-columns: repeat(1, minmax(0, 1fr));
  }
}

.more-indicator {
  position: absolute;
  top: 0;
  font-size: 14px;
  font-weight: bold;
  background: white;
  border-radius: 9999px;
  padding: 0 6px;
  line-height: 30px;
  height: 30px;
  white-space: nowrap;
}

:deep(.el-popper) {
  border-radius: 8px !important;
  padding: 5px 10px !important;
  width: 150px !important;
}
</style>
